import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import apiService from '../../../services/api';
import DetailPopup from '../../../components/DetailPopup';
import CompetitorAnalysisModal from '../../asis/components/CompetitorAnalysisModal';
import '../../asis/components/CompetitorAnalysisModal.css';
import { getParamLabel } from '../../../constants/parameterLabels';
import { SEO_AS_IS_TABS } from '../../../constants/telemetryModules';
import { usePageTracking } from '../../../hooks/usePageTracking';

// ── Types ──────────────────────────────────────────────────────────────────

type TabId = 'onpage' | 'offpage' | 'technical';
type FilterId = 'all' | 'optimal' | 'needs_attention' | 'critical';

interface GovScore {
  score: number;
  max_score: number;
  status: string;
  snapshot_date: string | null;
  value?: string;
}

interface GovDelta {
  points: number;
  direction: 'improved' | 'regressed' | 'unchanged';
}

interface GovSubParam {
  parameter_group: string;
  sub_parameter: string;
  is_group_summary: false;
  baseline: GovScore;
  current: GovScore | null;
  delta: GovDelta | null;
}

interface GovGroupSummary {
  parameter_group: string;
  sub_parameter: null;
  is_group_summary: true;
  baseline: GovScore;
  current: GovScore | null;
  delta: GovDelta | null;
}

interface GovernanceTabData {
  parameters: GovSubParam[];
  group_summaries: GovGroupSummary[];
  total: number;
  improved_count: number;
  with_current_count: number;
}

interface GovernanceAsIsPerformanceProps {
  userId: string;
  onTooltipShow: (title: string, content: string) => void;
}

// ── Group Config ───────────────────────────────────────────────────────────

const GROUP_CONFIG: Record<string, { label: string; icon: string; tab: TabId }> = {
  serp_snippet_optimization:      { label: 'SERP Snippet Optimization',              icon: 'ri-search-eye-line',   tab: 'onpage'    },
  content_structure_hierarchy:    { label: 'Content Structure & Hierarchy',          icon: 'ri-list-ordered',      tab: 'onpage'    },
  accessibility_optimization:     { label: 'Accessibility Optimization',             icon: 'ri-eye-line',          tab: 'onpage'    },
  url_page_signals:               { label: 'URL & Page-Level Signals',               icon: 'ri-link',              tab: 'onpage'    },
  domain_authority_trust:         { label: 'Domain Authority & Trust Signals',       icon: 'ri-shield-check-line', tab: 'offpage'   },
  backlink_relevance:             { label: 'Backlink Relevance & Topic Alignment',   icon: 'ri-links-line',        tab: 'offpage'   },
  anchor_text_balance:            { label: 'Anchor Text Profile & Risk Balance',     icon: 'ri-text',              tab: 'offpage'   },
  brand_entity_authority:         { label: 'Brand Mentions & Entity Authority',      icon: 'ri-megaphone-line',    tab: 'offpage'   },
  crawl_indexation:               { label: 'Crawl & Indexation',                     icon: 'ri-search-line',       tab: 'technical' },
  canonicalization_duplicate:     { label: 'Canonicalization & Duplicate Control',   icon: 'ri-file-copy-line',    tab: 'technical' },
  page_experience_performance:    { label: 'Core Web Vitals (CWV)',                  icon: 'ri-speed-line',        tab: 'technical' },
  search_rendering_accessibility: { label: 'Search Rendering Accessibility',         icon: 'ri-code-box-line',     tab: 'technical' },
  ai_llm_crawl:                   { label: 'AI & LLM Crawl',                         icon: 'ri-robot-line',        tab: 'technical' },
};

const TAB_GROUPS: Record<TabId, string[]> = {
  onpage:    ['serp_snippet_optimization', 'content_structure_hierarchy', 'accessibility_optimization', 'url_page_signals'],
  offpage:   ['domain_authority_trust', 'backlink_relevance', 'anchor_text_balance', 'brand_entity_authority'],
  technical: ['page_experience_performance', 'crawl_indexation', 'canonicalization_duplicate', 'search_rendering_accessibility', 'ai_llm_crawl'],
};

// ── SERP Feature Catalog (BRD 15-Feature Taxonomy) ─────────────────────────

const SERP_FEATURE_CATALOG: Array<{
  key: string;
  name: string;
  type: string;
  paid: boolean;
  icon: string;
  description: string;
}> = [
  { key: 'featured_snippet', name: 'Featured Snippet', type: 'Organic', paid: false, icon: 'ri-star-line', description: 'Direct answer box at the top of results.' },
  { key: 'ai_overview', name: 'AI Overview', type: 'Organic/Generative', paid: false, icon: 'ri-ai-generate', description: "Google's AI-generated summary answer." },
  { key: 'people_also_ask', name: 'People Also Ask', type: 'Organic', paid: false, icon: 'ri-question-line', description: 'Related expandable follow-up questions.' },
  { key: 'local_pack', name: 'Local Pack', type: 'Organic/Local', paid: false, icon: 'ri-map-pin-line', description: 'Map with local business listings.' },
  { key: 'knowledge_graph', name: 'Knowledge Panel', type: 'Organic/Entity', paid: false, icon: 'ri-book-line', description: 'Entity information panel shown on search results.' },
  { key: 'image_pack', name: 'Image Pack / Carousel', type: 'Organic', paid: false, icon: 'ri-image-line', description: 'Image-heavy visual SERP block.' },
  { key: 'video_results', name: 'Video Results / Carousel', type: 'Organic', paid: false, icon: 'ri-video-line', description: 'Video thumbnail result carousel.' },
  { key: 'top_stories', name: 'Top Stories / News Box', type: 'Organic/News', paid: false, icon: 'ri-newspaper-line', description: 'News-focused recent story cluster.' },
  { key: 'rich_snippets', name: 'Rich Snippets', type: 'Organic/Rich', paid: false, icon: 'ri-list-check-2', description: 'Enhanced snippets (ratings, prices, dates).' },
  { key: 'site_links', name: 'Sitelinks', type: 'Organic', paid: false, icon: 'ri-links-line', description: 'Sub-page links shown under primary result.' },
  { key: 'discussions_forums', name: 'Discussions & Forums', type: 'Organic/Community', paid: false, icon: 'ri-chat-3-line', description: 'Forum/community results like Reddit threads.' },
  { key: 'jobs_listing', name: 'Jobs Listing', type: 'Organic', paid: false, icon: 'ri-briefcase-line', description: 'Aggregated jobs block in SERP.' },
  { key: 'flights_hotels', name: 'Flights / Hotels Box', type: 'Organic/Vertical', paid: false, icon: 'ri-plane-line', description: 'Travel booking SERP integrations.' },
  { key: 'paid_ads', name: 'Paid Ads', type: 'Paid', paid: true, icon: 'ri-money-dollar-circle-line', description: 'Sponsored text ad placements.' },
  { key: 'shopping_results', name: 'Shopping Ads', type: 'Paid', paid: true, icon: 'ri-shopping-bag-line', description: 'Product listing ads with images/prices.' },
];

const SERP_CORE_SUMMARY_FEATURES = [
  'featured_snippet',
  'knowledge_graph',
  'local_pack',
  'image_pack',
  'video_results',
  'people_also_ask',
] as const;

const SERP_FEATURE_META = SERP_FEATURE_CATALOG.reduce((acc, item) => {
  acc[item.key] = item;
  return acc;
}, {} as Record<string, { key: string; name: string; type: string; paid: boolean; icon: string; description: string }>);

const humanizeSerpFeature = (key: string) =>
  key
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());

// ── Helpers ────────────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: string }) {
  const s = (status || '').toLowerCase();
  const cls   = s === 'optimal' ? 'bg-green-100 text-green-700'  : s === 'critical' ? 'bg-red-100 text-red-700'  : 'bg-yellow-100 text-yellow-700';
  const dot   = s === 'optimal' ? 'bg-green-500'                 : s === 'critical' ? 'bg-red-500'               : 'bg-yellow-500';
  const label = s === 'optimal' ? 'Optimal'                      : s === 'critical' ? 'Critical'                 : 'Needs Attention';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${cls}`}>
      <span className={`w-1.5 h-1.5 rounded-full mr-1.5 ${dot}`}></span>
      {label}
    </span>
  );
}

function formatDate(d: string | null) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function deriveGroupStatus(subParams: GovSubParam[]): 'optimal' | 'needs_attention' | 'critical' {
  let optimalCount = 0;
  let needsAttentionCount = 0;
  let criticalCount = 0;

  for (const sp of subParams) {
    const s = ((sp.current?.status ?? sp.baseline.status) || '').toLowerCase();
    if (s === 'critical') criticalCount++;
    else if (s === 'needs_attention' || s === 'needs-attention') needsAttentionCount++;
    else optimalCount++;
  }

  const max = Math.max(optimalCount, needsAttentionCount, criticalCount);

  if (criticalCount === max && criticalCount > 0) return 'critical';
  if (needsAttentionCount === max && needsAttentionCount > 0) return 'needs_attention';
  return 'optimal';
}

function statusToFilterId(status: string): FilterId {
  const s = (status || '').toLowerCase();
  if (s === 'optimal') return 'optimal';
  if (s === 'critical') return 'critical';
  return 'needs_attention';
}

// ── Component ──────────────────────────────────────────────────────────────

export default function GovernanceAsIsPerformance({ userId, onTooltipShow }: GovernanceAsIsPerformanceProps) {
  const navigate = useNavigate();

  // ── Navigation & filter state ────────────────────────────────────────
  const [activeTab, setActiveTab]           = useState<TabId>('onpage');
  usePageTracking({
    moduleName: 'SEO Manager',
    submoduleName: `AS-IS Performance / ${SEO_AS_IS_TABS[activeTab]}`,
    routePath: `/dashboard#as-is-performance/${activeTab}`,
  });
  const [performanceFilter, setPerformanceFilter] = useState<FilterId>('all');
  const [expandedGroups, setExpandedGroups] = useState<string[]>([]);

  // ── Summary data (for metric cards 1–3) ──────────────────────────────
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [summaryData, setSummaryData]       = useState<any>(null);
  const [siteDomain, setSiteDomain]         = useState<string>('');
  const [gscConnected, setGscConnected]     = useState<boolean>(true);
  const [ga4Connected, setGa4Connected]     = useState<boolean>(true);
  const [connectionsLoaded, setConnectionsLoaded] = useState<boolean>(false);

  // ── Governance data (cached per tab) ─────────────────────────────────
  const [govData, setGovData] = useState<Record<TabId, GovernanceTabData | null | 'no_baseline' | 'error'>>({
    onpage: null, offpage: null, technical: null,
  });
  const [govLoading, setGovLoading]     = useState(false);
  const [refreshing, setRefreshing]     = useState(false);
  const [refreshError, setRefreshError] = useState<string | null>(null);
  const [lastRefreshed, setLastRefreshed] = useState<Record<TabId, string | null>>({
    onpage: null, offpage: null, technical: null,
  });

  // ── Popup state ───────────────────────────────────────────────────────
  const [isKeywordPopupOpen,    setIsKeywordPopupOpen]    = useState(false);
  const [isSerpPopupOpen,       setIsSerpPopupOpen]       = useState(false);
  const [serpTab,               setSerpTab]               = useState<'features'|'keywords'>('features');
  const [isBlogPopupOpen,       setIsBlogPopupOpen]       = useState(false);
  const [isCompetitorModalOpen, setIsCompetitorModalOpen] = useState(false);

  const [liveKeywords, setLiveKeywords] = useState<any[]>([]);

  // ── Fetch summary data ────────────────────────────────────────────────
  useEffect(() => {
    if (!userId) return;
    const doFetch = async () => {
      setSummaryLoading(true);
      setConnectionsLoaded(false);
      try {
        const [oauthResult, profileResult, universeResult] = await Promise.allSettled([
          apiService.checkOAuthStatus(userId),
          apiService.getUserProfile(userId),
          apiService.getKeywordUniverse(userId)
        ]);

        if (oauthResult.status === 'fulfilled') {
          const oauthStatus = oauthResult.value;
          setGscConnected(Boolean(oauthStatus.gsc_connected));
          setGa4Connected(Boolean(oauthStatus.ga4_connected));
        } else {
          console.warn("Could not check oauth status", oauthResult.reason);
        }
        setConnectionsLoaded(true);

        let siteUrl: string | null = null;
        if (profileResult.status === 'fulfilled') {
          const profileData = profileResult.value?.data || profileResult.value;
          siteUrl = profileData?.website_url || null;
          if (siteUrl) {
            const domain = siteUrl.replace(/^https?:\/\//, '').replace(/\/$/, '');
            setSiteDomain(domain);
          }
        }

        let trackedKeywords: string[] = [];
        if (universeResult.status === 'fulfilled') {
          const ud = universeResult.value?.data || universeResult.value;
          let items: any[] = [];
          if (ud?.keywords && Array.isArray(ud.keywords)) {
            // V1 flat array
            items = ud.keywords;
          } else if (ud?.keywords && typeof ud.keywords === 'object') {
            // V2 format: keywords is { seo: [...], gmb: [...] }
            const kwObj = ud.keywords as Record<string, any[]>;
            const combined = [
              ...(Array.isArray(kwObj.seo) ? kwObj.seo : []),
              ...(Array.isArray(kwObj.gmb) ? kwObj.gmb : []),
            ];
            items = combined.length > 0 ? combined : Object.values(kwObj).flat();
          } else if (ud?.items && Array.isArray(ud.items)) {
            items = ud.items;
          } else if (Array.isArray(ud)) {
            items = ud;
          }
          const validItems = items.filter((i: any) => i.is_selected !== false && (i.is_active !== false || i.is_selected));
          setLiveKeywords(validItems);
          trackedKeywords = validItems.map((i: any) => i.keyword).filter(Boolean);
        }

        const summaryRes = await apiService.getAsIsSummary({
          user_id: userId,
          site_url: siteUrl,
          tracked_keywords: trackedKeywords,
          is_governance: true,
        }) as any;
        setSummaryData(summaryRes?.data || summaryRes || null);
      } catch {
        /* leave null */
      } finally {
        setSummaryLoading(false);
      }
    };
    doFetch();
  }, [userId]);

  // ── Fetch governance params for a tab ────────────────────────────────
  const fetchGovParams = useCallback(async (tab: TabId) => {
    if (!userId) return;
    setGovLoading(true);
    try {
      const json = await apiService.getGovernanceParameters(userId, tab) as any;
      const data = json?.data;
      if (!data) { setGovData(prev => ({ ...prev, [tab]: 'error' })); return; }
      if (data.error === 'no_baseline') {
        setGovData(prev => ({ ...prev, [tab]: 'no_baseline' }));
        return;
      }
      setGovData(prev => ({
        ...prev,
        [tab]: {
          parameters:         data.parameters         ?? [],
          group_summaries:    data.group_summaries     ?? [],
          total:              data.total               ?? 0,
          improved_count:     data.improved_count      ?? 0,
          with_current_count: data.with_current_count  ?? 0,
        } as GovernanceTabData,
      }));
    } catch {
      setGovData(prev => ({ ...prev, [tab]: 'error' }));
    } finally {
      setGovLoading(false);
    }
  }, [userId]);

  // Fetch active tab on mount; subsequent tabs fetched on demand
  useEffect(() => {
    if (userId && govData['onpage'] === null) {
      fetchGovParams('onpage');
    }
  }, [userId]);

  const handleTabChange = (tab: TabId) => {
    setActiveTab(tab);
    setPerformanceFilter('all');
    if (govData[tab] === null) {
      fetchGovParams(tab);
    }
  };

  const handleRefresh = async () => {
    if (!userId || refreshing) return;
    setRefreshing(true);
    setRefreshError(null);
    try {
      const [json, summaryRes] = await Promise.all([
        apiService.refreshGovernanceTab(userId, activeTab) as any,
        apiService.getAsIsSummary({
          user_id: userId,
          site_url: siteDomain || null,
          tracked_keywords: liveKeywords.map((k: any) => k.keyword),
          is_governance: true,
          force_refresh: true,
        }) as any
      ]);
      const data = json?.data;
      if (data && data.error !== 'no_baseline' && data.parameters) {
        setGovData(prev => ({
          ...prev,
          [activeTab]: {
            parameters:         data.parameters         ?? [],
            group_summaries:    data.group_summaries     ?? [],
            total:              data.total               ?? 0,
            improved_count:     data.improved_count      ?? 0,
            with_current_count: data.with_current_count  ?? 0,
          } as GovernanceTabData,
        }));
      }
      if (summaryRes && (summaryRes.data || summaryRes)) {
        setSummaryData(summaryRes.data || summaryRes);
      }
      const now = new Date();
      const ts = now.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
        + ', ' + now.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
      setLastRefreshed(prev => ({ ...prev, [activeTab]: ts }));
    } catch (e: any) {
      setRefreshError(e?.message || 'Refresh failed');
    } finally {
      setRefreshing(false);
    }
  };

  const toggleGroup = (groupId: string) => {
    setExpandedGroups(prev =>
      prev.includes(groupId) ? prev.filter(id => id !== groupId) : [...prev, groupId]
    );
  };

  // ── Derived: active tab data ──────────────────────────────────────────
  const activeGovData = govData[activeTab];
  const isTabData     = activeGovData !== null && activeGovData !== 'no_baseline' && activeGovData !== 'error';
  const hasNoBaseline = activeGovData === 'no_baseline';
  const hasError      = activeGovData === 'error';

  // Group sub-parameters by group_id for current tab
  const groupedSubParams: Map<string, GovSubParam[]> = new Map();
  if (isTabData) {
    for (const sp of (activeGovData as GovernanceTabData).parameters) {
      const existing = groupedSubParams.get(sp.parameter_group);
      if (existing) existing.push(sp);
      else groupedSubParams.set(sp.parameter_group, [sp]);
    }
  }

  // Group summaries lookup
  const groupSummaryMap: Map<string, GovGroupSummary> = new Map();
  if (isTabData) {
    for (const gs of (activeGovData as GovernanceTabData).group_summaries) {
      groupSummaryMap.set(gs.parameter_group, gs);
    }
  }

  // Tab group counts (number of groups we have data for)
  const getTabCount = (tab: TabId) => {
    const d = govData[tab];
    if (!d || d === 'no_baseline' || d === 'error') return TAB_GROUPS[tab].length;
    return (d as GovernanceTabData).group_summaries.length;
  };

  // ── Filter logic ──────────────────────────────────────────────────────
  const getFilteredGroupIds = (): string[] => {
    const groupIds = TAB_GROUPS[activeTab];
    if (!isTabData || performanceFilter === 'all') return groupIds;
    return groupIds.filter(gid => {
      const subParams = groupedSubParams.get(gid) || [];
      return subParams.some(sp => {
        const effectiveStatus = ((sp.current?.status ?? sp.baseline.status) || '').toLowerCase();
        if (performanceFilter === 'critical') return effectiveStatus === 'critical';
        if (performanceFilter === 'optimal') return effectiveStatus === 'optimal';
        return effectiveStatus === 'needs_attention' || effectiveStatus === 'needs-attention';
      });
    });
  };

  const getFilteredSubParams = (groupId: string): GovSubParam[] => {
    const subParams = groupedSubParams.get(groupId) || [];
    if (performanceFilter === 'all') return subParams;
    return subParams.filter(sp => {
      const effectiveStatus = ((sp.current?.status ?? sp.baseline.status) || '').toLowerCase();
      if (performanceFilter === 'critical') return effectiveStatus === 'critical';
      if (performanceFilter === 'optimal') return effectiveStatus === 'optimal';
      return effectiveStatus === 'needs_attention' || effectiveStatus === 'needs-attention';
    });
  };

  // ── Prepare Metrics (Cards 1-3) ────────────────────────────────────────
  const rawKwPerf = summaryData?.keywords_performance || summaryData?.keyword_rankings || null;
  
  const parsePos = (val: any): number => {
    if (typeof val === 'number') return val > 0 ? val : 0;
    if (typeof val === 'string') {
      if (val.includes('>')) return 0;
      const num = parseFloat(val.replace(/[^\d.]/g, ''));
      return !isNaN(num) && num > 0 ? num : 0;
    }
    return 0;
  };

  const formatAvgDuration = (seconds?: number | null, str?: string | null) => {
    if (str && str !== '—') return str;
    if (!seconds || Number.isNaN(seconds) || seconds <= 0) return '—';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    if (mins <= 0) return `${secs}s`;
    return `${mins}m ${secs}s`;
  };

  // Combine distinct keywords from liveKeywords and rawKwPerf.ranked_keywords
  const allKeywordItems: any[] = [...liveKeywords];
  const existingKwSet = new Set(liveKeywords.map((k: any) => String(k.keyword || '').toLowerCase().trim()));
  if (Array.isArray(rawKwPerf?.ranked_keywords)) {
    for (const rk of rawKwPerf.ranked_keywords) {
      const norm = String(rk.keyword || '').toLowerCase().trim();
      if (norm && !existingKwSet.has(norm)) {
        allKeywordItems.push(rk);
        existingKwSet.add(norm);
      }
    }
  }

  // Construct unified keyword list with accurate rankings
  const mergedKeywords = (allKeywordItems.length > 0 ? allKeywordItems : (rawKwPerf?.ranked_keywords || [])).map((lk: any) => {
    const rawMatch = rawKwPerf?.ranked_keywords?.find(
      (rk: any) => String(rk.keyword || '').toLowerCase().trim() === String(lk.keyword || '').toLowerCase().trim()
    );
    const lkPos = parsePos(lk.current_position || lk.position || lk.rank);
    const rawPos = parsePos(rawMatch?.position || rawMatch?.current_position || rawMatch?.rank);
    const posVal = lkPos > 0 ? lkPos : rawPos;
    const isNearMe = String(lk.keyword || '').toLowerCase().includes('near me') || lk.position === 'N/A' || rawMatch?.position === 'N/A';

    return {
      keyword: lk.keyword,
      positionVal: isNearMe ? -1 : posVal,
      position: isNearMe ? 'N/A' : (posVal > 0 && posVal <= 100 ? (Number.isInteger(posVal) ? posVal : posVal.toFixed(1)) : '>100'),
      search_volume: lk.search_volume || lk.volume || rawMatch?.search_volume || '—',
      keyword_difficulty: lk.keyword_difficulty || lk.kd || rawMatch?.keyword_difficulty || '—',
      intent: lk.intent || rawMatch?.intent || '—',
    };
  }).sort((a: any, b: any) => {
    const getSortWeight = (item: any) => {
      const kw = String(item.keyword || '').toLowerCase();
      if (kw.includes('near me') || item.position === 'N/A' || item.position === 'NA') {
        return 2000000; // Bottom-most
      }
      if (item.positionVal > 0 && item.positionVal <= 100) {
        return item.positionVal; // Ranked top
      }
      return 1000000; // Unranked >100 in middle
    };
    const diff = getSortWeight(a) - getSortWeight(b);
    if (diff !== 0) return diff;
    return a.keyword.localeCompare(b.keyword);
  });

  const validRanked = mergedKeywords.filter((k: any) => k.positionVal > 0 && k.positionVal <= 100);
  const calculatedAvgPos = validRanked.length > 0
    ? (validRanked.reduce((sum: number, k: any) => sum + k.positionVal, 0) / validRanked.length)
    : (rawKwPerf?.avg_position || 0);

  const kwMetrics = (mergedKeywords.length > 0 || rawKwPerf) ? {
    avg_position: calculatedAvgPos,
    tracked_keywords_count: mergedKeywords.length || liveKeywords.length || rawKwPerf?.tracked_keywords_count || 0,
    data_available: true,
    ranked_keywords: mergedKeywords,
  } : null;
  const rawSerpMetrics = summaryData?.serp_features ?? null;
  const featureStatusMap: Record<string, string> = rawSerpMetrics?.feature_status_map || {};
  const hasStatusMap = Object.keys(featureStatusMap).length > 0;

  // Process all 15 catalog items with status awareness
  const serpFeatureList = SERP_FEATURE_CATALOG.map((catalogItem) => {
    const key = catalogItem.key;
    const meta = SERP_FEATURE_META[key] || catalogItem;
    let status = 'not_applicable';

    if (hasStatusMap) {
      status = featureStatusMap[key] || 'not_applicable';
    } else if (rawSerpMetrics) {
      const targetPresence = rawSerpMetrics.target_feature_presence || {};
      const activeCount = Number(targetPresence[key] || 0);
      const featuresPresent = Array.isArray(rawSerpMetrics.features_present) ? rawSerpMetrics.features_present : [];
      const inSerp = featuresPresent.includes(key) || activeCount > 0;
      status = activeCount > 0 ? 'active' : inSerp ? 'missing' : 'not_applicable';
    }

    return {
      key,
      name: meta.name,
      type: meta.type,
      paid: meta.paid,
      icon: meta.icon,
      description: meta.description,
      status,
      present: status === 'active',
      missing: status === 'missing',
      notApplicable: status === 'not_applicable',
    };
  });

  const serpActiveCount = rawSerpMetrics?.active_count ?? serpFeatureList.filter((f) => f.present).length;
  const serpMissingCount = rawSerpMetrics?.missing_count ?? serpFeatureList.filter((f) => f.missing).length;
  const serpNotApplicableCount = rawSerpMetrics?.not_applicable_count ?? serpFeatureList.filter((f) => f.notApplicable).length;
  const serpApplicableCount = rawSerpMetrics?.applicable_count ?? (serpActiveCount + serpMissingCount);

  // Baseline -> Current -> Delta
  const baselineActiveCount = rawSerpMetrics?.baseline_features_count ?? serpActiveCount;
  const serpDelta = serpActiveCount - baselineActiveCount;

  const serpMetrics = rawSerpMetrics ? {
    ...rawSerpMetrics,
    keyword_details: (rawSerpMetrics.keyword_details && rawSerpMetrics.keyword_details.length > 0)
      ? rawSerpMetrics.keyword_details
      : liveKeywords.map((k: any) => ({
          keyword: k.keyword,
          features: [],
          your_presence: [],
          rank: k.current_position > 0 ? `#${k.current_position}` : 'Unranked'
        }))
  } : liveKeywords.length > 0 ? {
    features_count: 0,
    target_feature_presence: {},
    keyword_details: liveKeywords.map((k: any) => ({
      keyword: k.keyword,
      features: [],
      your_presence: [],
      rank: k.current_position > 0 ? `#${k.current_position}` : 'Unranked'
    })),
    data_available: true
  } : null;
  const trafficMetrics = summaryData?.organic_traffic ? {
    ...summaryData.organic_traffic,
    avg_session_duration: formatAvgDuration(
      summaryData.organic_traffic.avg_duration_seconds,
      summaryData.organic_traffic.avg_session_duration || summaryData.organic_traffic.avg_duration
    )
  } : null;

  // Card 4: AS-IS Progress (from active tab governance data)
  const progressData = isTabData ? (activeGovData as GovernanceTabData) : null;

  // ── JSX ────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6">

      {/* Header */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">AS-IS Performance</h2>
            {(govLoading || summaryLoading) && (
              <span className="ml-4 text-sm text-blue-500 font-medium animate-pulse flex items-center">
                <i className="ri-loader-4-line animate-spin mr-1"></i>
                {summaryLoading ? 'Checking Database...' : 'Loading Parameters...'}
              </span>
            )}
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={handleRefresh}
              disabled={refreshing || hasNoBaseline}
              className={`flex items-center space-x-1.5 px-4 py-2 rounded-lg border text-sm font-medium transition-all duration-200 ${
                refreshing || hasNoBaseline
                  ? 'border-gray-200 dark:border-gray-600 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                  : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              <i className={`ri-refresh-line text-sm ${refreshing ? 'animate-spin' : ''}`}></i>
              <span className="whitespace-nowrap">{refreshing ? 'Refreshing…' : 'Refresh Current Data'}</span>
            </button>
            <button
              onClick={() => onTooltipShow('AS-IS Performance', 'This governance view compares your locked baseline SEO scores against current re-scan scores. Use Refresh Current Data to run a new scan and populate the Current and Change columns.')}
              className="text-gray-400 hover:text-blue-500"
            >
              <i className="ri-information-line text-xl"></i>
            </button>
          </div>
        </div>
        <p className="text-gray-600 dark:text-gray-400">Track how your SEO signal scores have changed since your baseline was captured</p>
        {lastRefreshed[activeTab] && (
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
            Current data as of {lastRefreshed[activeTab]}
          </p>
        )}
        {refreshError && (
          <p className="text-xs text-red-500 mt-1">{refreshError}</p>
        )}
      </div>

      {/* ── Metric Cards ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4 gap-4">

        {/* Card 1 — Keyword Rankings */}
        <div
          onClick={() => setIsKeywordPopupOpen(true)}
          className="bg-white dark:bg-gray-800 rounded-2xl p-5 shadow-sm border border-gray-100 dark:border-gray-700 cursor-pointer hover:shadow-md transition-shadow group"
        >
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 rounded-xl bg-teal-500 flex items-center justify-center group-hover:scale-110 transition-transform">
                <span className="text-white font-bold text-lg">O<sub className="text-xs">IT</sub></span>
              </div>
              <div>
                <h3 className="text-base font-bold text-gray-900 dark:text-white">Keyword<br />Rankings</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {!connectionsLoaded || (summaryLoading && !summaryData)
                    ? 'Loading…'
                    : gscConnected
                    ? (kwMetrics ? `${kwMetrics.tracked_keywords_count || 0} keywords` : 'Loading…')
                    : 'GSC not connected'}
                </p>
              </div>
            </div>
            <button onClick={e => { e.stopPropagation(); onTooltipShow('Keyword Rankings', 'This shows where your website appears in search results for your target keywords.'); }} className="text-gray-400 hover:text-blue-500">
              <i className="ri-information-line text-sm"></i>
            </button>
          </div>
          {!connectionsLoaded || (summaryLoading && !summaryData) ? (
            <div className="flex flex-col items-center justify-center py-6 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500 mb-2"></div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Loading data…</p>
            </div>
          ) : !gscConnected ? (
            <div className="flex flex-col items-center justify-center py-6 text-center">
              <i className="ri-key-line text-3xl text-gray-300 dark:text-gray-600 mb-2"></i>
              <p className="text-sm text-gray-500 dark:text-gray-400">Connect GSC to fetch the data</p>
            </div>
          ) : kwMetrics?.data_available ? (
            <>
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div className="text-center">
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">#{kwMetrics.avg_position?.toFixed(1)}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Avg Pos</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">{kwMetrics.tracked_keywords_count || 0}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Tracked</p>
                </div>
              </div>
              <div className="space-y-2">
                {(kwMetrics.ranked_keywords || []).slice(0, 5).map((kw: any, i: number) => {
                  const isNearMe = String(kw.keyword || '').toLowerCase().includes('near me');
                  const isNA = isNearMe || kw.position === 'N/A' || kw.position === 'NA';
                  return (
                    <div key={i} className="flex items-center justify-between text-sm">
                      <span className="text-gray-900 dark:text-white truncate flex-1" title={kw.keyword}>{kw.keyword}</span>
                      <span className={`font-bold ml-2 ${isNA ? 'text-gray-400' : 'text-teal-600'}`}>
                        {isNA ? 'N/A' : /^\d+(\.\d+)?$/.test(String(kw.position)) ? `#${kw.position}` : kw.position}
                      </span>
                    </div>
                  );
                })}
              </div>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center py-6 text-center">
              <i className="ri-key-line text-3xl text-gray-300 dark:text-gray-600 mb-2"></i>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                No ranking data yet
              </p>
            </div>
          )}
        </div>

        {/* Card 2 — Captured Visibility (SERP Positioning) */}
        <div
          onClick={() => setIsSerpPopupOpen(true)}
          className="bg-white dark:bg-gray-800 rounded-2xl p-5 shadow-sm border border-gray-100 dark:border-gray-700 cursor-pointer hover:shadow-md transition-shadow group"
        >
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 rounded-xl bg-purple-500 flex items-center justify-center group-hover:scale-110 transition-transform">
                <i className="ri-layout-grid-line text-white text-xl"></i>
              </div>
              <div>
                <h3 className="text-base font-bold text-gray-900 dark:text-white">Captured<br />Visibility</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {rawSerpMetrics?.data_available
                    ? `Applicable: ${serpApplicableCount}`
                    : 'SERP features'}
                </p>
              </div>
            </div>
            <button onClick={e => { e.stopPropagation(); onTooltipShow('Captured Visibility', 'Tracks your brand presence across search features (Featured Snippets, Knowledge Panels, etc.) and monitors change against your baseline.'); }} className="text-gray-400 hover:text-blue-500">
              <i className="ri-information-line text-sm"></i>
            </button>
          </div>
          <>
            {/* Baseline -> Current -> Delta Comparison Row */}
            <div className="grid grid-cols-3 gap-2 mb-3 bg-gray-50 dark:bg-gray-700/50 p-2.5 rounded-xl border border-gray-100 dark:border-gray-700">
              <div className="text-center">
                <p className="text-lg font-bold text-gray-700 dark:text-gray-200">
                  {baselineActiveCount} <span className="text-xs font-normal text-gray-400">/ {serpApplicableCount}</span>
                </p>
                <p className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400 mt-0.5">Baseline</p>
              </div>
              <div className="text-center">
                <p className="text-lg font-bold text-teal-600 dark:text-teal-400">
                  {serpActiveCount} <span className="text-xs font-normal text-teal-400/70">/ {serpApplicableCount}</span>
                </p>
                <p className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400 mt-0.5">Current</p>
              </div>
              <div className="text-center">
                <p className={`text-lg font-bold ${
                  serpDelta > 0
                    ? 'text-green-600 dark:text-green-400'
                    : serpDelta < 0
                    ? 'text-red-600 dark:text-red-400'
                    : 'text-gray-400'
                }`}>
                  {serpDelta > 0 ? `+${serpDelta}` : serpDelta === 0 ? '0' : `${serpDelta}`}
                </p>
                <p className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400 mt-0.5">Change (Δ)</p>
              </div>
            </div>

            {/* Core 6 Summary Features */}
            <div className="space-y-1.5">
              {serpFeatureList
                .filter(f => SERP_CORE_SUMMARY_FEATURES.includes(f.key as any))
                .map(f => {
                  const dataAvailable = rawSerpMetrics?.data_available ?? false;
                  return (
                    <div key={f.key} className="flex items-center justify-between text-xs">
                      <div className="flex items-center space-x-1.5 min-w-0">
                        <i className={`${f.icon} ${f.present ? 'text-teal-500' : f.missing ? 'text-amber-500' : 'text-gray-400'} flex-shrink-0`}></i>
                        <span className="text-gray-700 dark:text-gray-300 truncate">{f.name}</span>
                      </div>
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium flex-shrink-0 ${
                        f.present
                          ? 'bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300'
                          : f.missing
                          ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                      }`}>
                        {f.present ? 'Active' : f.missing ? 'Missing' : 'N/A'}
                      </span>
                    </div>
                  );
                })}
            </div>
            {!(rawSerpMetrics?.data_available ?? false) && (
              <p className="text-[10px] text-gray-400 italic text-center pt-1">
                SERP features will populate once ranking data is available
              </p>
            )}
          </>
        </div>

        {/* Card 3 — Organic Traffic */}
        <div
          onClick={() => setIsBlogPopupOpen(true)}
          className="bg-white dark:bg-gray-800 rounded-2xl p-5 shadow-sm border border-gray-100 dark:border-gray-700 cursor-pointer hover:shadow-md transition-shadow group"
        >
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 rounded-xl bg-teal-500 flex items-center justify-center group-hover:scale-110 transition-transform">
                <i className="ri-user-line text-white text-xl"></i>
              </div>
              <div>
                <h3 className="text-base font-bold text-gray-900 dark:text-white">Organic<br />Traffic</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {!connectionsLoaded || (summaryLoading && !summaryData)
                    ? 'Loading…'
                    : (!gscConnected && !ga4Connected)
                    ? 'GA & GSC not connected'
                    : (gscConnected && !ga4Connected)
                    ? 'GSC Connected • GA Not Connected'
                    : (!gscConnected && ga4Connected)
                    ? 'GA Connected • GSC Not Connected'
                    : 'Last 30 days'}
                </p>
              </div>
            </div>
            <button onClick={e => { e.stopPropagation(); onTooltipShow('Organic Traffic', 'Measures visitors who find your site through unpaid search results.'); }} className="text-gray-400 hover:text-blue-500">
              <i className="ri-information-line text-sm"></i>
            </button>
          </div>
          {!connectionsLoaded || (summaryLoading && !summaryData) ? (
            <div className="flex flex-col items-center justify-center py-6 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500 mb-2"></div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Loading data…</p>
            </div>
          ) : !gscConnected ? (
            <div className="flex flex-col items-center justify-center py-6 text-center">
              <i className="ri-bar-chart-line text-3xl text-gray-300 dark:text-gray-600 mb-2"></i>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {!ga4Connected ? 'Connect GA and GSC to fetch the data' : 'Connect GSC to fetch traffic data'}
              </p>
            </div>
          ) : trafficMetrics?.data_available ? (
            <>
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div className="text-center">
                  <p className="text-2xl font-bold text-teal-600">
                    {trafficMetrics.total_clicks >= 1000000
                      ? `${(trafficMetrics.total_clicks / 1000000).toFixed(1)}M`
                      : trafficMetrics.total_clicks >= 1000
                      ? `${(trafficMetrics.total_clicks / 1000).toFixed(1)}K`
                      : trafficMetrics.total_clicks}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Clicks</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    {trafficMetrics.total_impressions >= 1000000
                      ? `${(trafficMetrics.total_impressions / 1000000).toFixed(1)}M`
                      : trafficMetrics.total_impressions >= 1000
                      ? `${(trafficMetrics.total_impressions / 1000).toFixed(1)}K`
                      : trafficMetrics.total_impressions}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Impressions</p>
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between mb-1">
                  <p className="text-xs font-semibold text-gray-700 dark:text-gray-300">Top Performing Pages</p>
                  <p className="text-xs font-semibold text-gray-500 dark:text-gray-400">Clicks</p>
                </div>
                {(trafficMetrics.top_pages || []).slice(0, 5).map((page: any, i: number) => (
                  <div key={i} className="flex items-center justify-between text-sm">
                    <span className="text-gray-900 dark:text-white truncate flex-1" title={page.page === '/' ? `${siteDomain}/` : `${siteDomain}${page.page}`}>
                      {page.page === '/' ? `${siteDomain}/` : `${siteDomain}${page.page}`}
                    </span>
                    <span className="font-bold text-gray-900 dark:text-white ml-2">
                      {page.visitors >= 1000 ? `${(page.visitors / 1000).toFixed(1)}K` : page.visitors}
                    </span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center py-6 text-center">
              <i className="ri-bar-chart-line text-3xl text-gray-300 dark:text-gray-600 mb-2"></i>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                No traffic data yet
              </p>
            </div>
          )}
        </div>

        {/* Card 4 — AS-IS Progress */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 shadow-sm border border-gray-100 dark:border-gray-700 group">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 rounded-xl bg-indigo-500 flex items-center justify-center group-hover:scale-110 transition-transform">
                <i className="ri-line-chart-line text-white text-xl"></i>
              </div>
              <div>
                <h3 className="text-base font-bold text-gray-900 dark:text-white">AS-IS<br />Progress</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 capitalize">{activeTab} tab</p>
              </div>
            </div>
            <button onClick={() => onTooltipShow('AS-IS Progress', 'Tracks how many sub-parameters have improved since the baseline was captured.')} className="text-gray-400 hover:text-blue-500">
              <i className="ri-information-line text-sm"></i>
            </button>
          </div>
          {govLoading ? (
            <div className="flex items-center justify-center py-6">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500"></div>
            </div>
          ) : hasNoBaseline ? (
            <div className="flex flex-col items-center justify-center py-4 text-center">
              <i className="ri-time-line text-3xl text-gray-300 dark:text-gray-600 mb-2"></i>
              <p className="text-sm font-medium text-gray-900 dark:text-white">Baseline pending</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Complete AS-IS analysis first</p>
            </div>
          ) : hasError ? (
            <div className="flex flex-col items-center justify-center py-4 text-center">
              <i className="ri-error-warning-line text-3xl text-red-400 mb-2"></i>
              <p className="text-sm text-gray-500 dark:text-gray-400">Failed to load</p>
            </div>
          ) : progressData ? (
            <>
              <div className="text-center mb-4">
                <p className="text-4xl font-bold text-green-600 dark:text-green-400">{progressData.improved_count}</p>
                <p className="text-sm font-medium text-gray-900 dark:text-white mt-1">Improved</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {progressData.with_current_count > 0
                    ? `of ${progressData.with_current_count} re-scanned`
                    : 'No re-scans yet'}
                </p>
              </div>
              <p className="text-center text-xs text-gray-500 dark:text-gray-400">{progressData.total} parameters tracked</p>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center py-6 text-center">
              <i className="ri-line-chart-line text-3xl text-gray-300 dark:text-gray-600 mb-2"></i>
              <p className="text-sm text-gray-500 dark:text-gray-400">Loading progress…</p>
            </div>
          )}
        </div>

        {/* Competitor Analysis Modal (for card clicks) */}
        <CompetitorAnalysisModal
          isOpen={isCompetitorModalOpen}
          onClose={() => setIsCompetitorModalOpen(false)}
          userId={userId}
        />
      </div>

      {/* ── Summary Bar ── */}
      {!hasNoBaseline && !hasError && progressData && (
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-200 dark:border-gray-700">
          {progressData.with_current_count === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400 text-center">
              No re-scans recorded yet for this tab
            </p>
          ) : (
            <>
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {progressData.improved_count} of {progressData.with_current_count} re-scanned parameters have improved since baseline
                </p>
                <span className="text-sm font-bold text-green-600 dark:text-green-400">
                  {Math.round((progressData.improved_count / progressData.with_current_count) * 100)}%
                </span>
              </div>
              <div className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                <div
                  className="h-full bg-green-500 rounded-full transition-all duration-500"
                  style={{ width: `${Math.round((progressData.improved_count / progressData.with_current_count) * 100)}%` }}
                ></div>
              </div>
            </>
          )}
        </div>
      )}

      {/* ── Performance Filter + Tab Navigation ── */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Performance Filter</h3>
          <div className="flex items-center space-x-2">
            {([
              { id: 'all',             label: 'Full View',       icon: 'ri-eye-line'              },
              { id: 'optimal',         label: 'Optimal',         icon: 'ri-checkbox-circle-line'  },
              { id: 'needs_attention', label: 'Needs Attention', icon: 'ri-error-warning-line'    },
              { id: 'critical',        label: 'Critical',        icon: 'ri-alert-line'            },
            ] as { id: FilterId; label: string; icon: string }[]).map(f => (
              <button
                key={f.id}
                onClick={() => setPerformanceFilter(f.id)}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-all duration-200 ${
                  performanceFilter === f.id
                    ? f.id === 'critical'
                      ? 'bg-red-600 text-white shadow-md'
                      : 'bg-blue-600 text-white shadow-md'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                <i className={`${f.icon} text-sm`}></i>
                <span className="text-sm font-medium whitespace-nowrap">{f.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex space-x-1 bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
          {([
            { id: 'onpage'    as TabId, label: 'On-Page SEO',                     icon: 'ri-file-text-line' },
            { id: 'offpage'   as TabId, label: 'Off-Page SEO',                    icon: 'ri-links-line'     },
            { id: 'technical' as TabId, label: 'Core Web Vitals & Technical SEO', icon: 'ri-speed-line'     },
          ]).map(tab => (
            <button
              key={tab.id}
              onClick={() => handleTabChange(tab.id)}
              className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-lg transition-all duration-200 ${
                activeTab === tab.id
                  ? 'bg-white dark:bg-gray-600 text-blue-600 dark:text-blue-400 shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              <i className={`${tab.icon} text-lg`}></i>
              <span className="font-medium whitespace-nowrap">{tab.label}</span>
              <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                activeTab === tab.id
                  ? 'bg-blue-100 text-blue-600'
                  : 'bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-400'
              }`}>{getTabCount(tab.id)}</span>
            </button>
          ))}
        </div>
      </div>

      {/* ── Content Area ── */}

      {/* Loading skeleton */}
      {govLoading && (
        <div className="animate-pulse space-y-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-700">
              <div className="flex items-center space-x-4 mb-4">
                <div className="w-10 h-10 bg-gray-200 dark:bg-gray-700 rounded-lg"></div>
                <div className="flex-1">
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/3 mb-2"></div>
                  <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/4"></div>
                </div>
              </div>
              <div className="space-y-2">
                {[1, 2, 3].map(j => (
                  <div key={j} className="h-3 bg-gray-200 dark:bg-gray-700 rounded"></div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* No baseline state */}
      {!govLoading && hasNoBaseline && (
        <div className="bg-white dark:bg-gray-800 rounded-xl p-8 shadow-lg border border-gray-200 dark:border-gray-700 text-center">
          <i className="ri-time-line text-4xl text-gray-300 dark:text-gray-600 mb-3 block"></i>
          <p className="text-gray-900 dark:text-white font-medium">Baseline not yet captured</p>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Complete your AS-IS analysis in Strategy to establish a baseline.
          </p>
        </div>
      )}

      {/* Error state */}
      {!govLoading && hasError && (
        <div className="bg-white dark:bg-gray-800 rounded-xl p-8 shadow-lg border border-gray-200 dark:border-gray-700 text-center">
          <i className="ri-error-warning-line text-4xl text-red-400 mb-3 block"></i>
          <p className="text-gray-900 dark:text-white font-medium">Failed to load parameters</p>
          <button
            onClick={() => fetchGovParams(activeTab)}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 transition-colors"
          >
            Retry
          </button>
        </div>
      )}

      {/* Parameter groups */}
      {!govLoading && isTabData && (
        <div className={`space-y-4 transition-opacity duration-300 ${refreshing ? 'opacity-60 pointer-events-none' : 'opacity-100'}`}>
          {getFilteredGroupIds().map(groupId => {
            const cfg          = GROUP_CONFIG[groupId];
            const subParams    = getFilteredSubParams(groupId);
            const groupSummary = groupSummaryMap.get(groupId);
            const isExpanded   = expandedGroups.includes(groupId);

            // If no sub-params in this group yet (no baseline data for it), skip
            if (!groupSummary && subParams.length === 0) return null;

            // Group status: always derive from sub-params (majority of currently visible)
            const groupStatus = subParams.length > 0
              ? deriveGroupStatus(subParams)
              : 'needs_attention';

            const iconBg    = groupStatus === 'optimal' ? 'bg-green-100 dark:bg-green-900/30'  : groupStatus === 'critical' ? 'bg-red-100 dark:bg-red-900/30'  : 'bg-yellow-100 dark:bg-yellow-900/30';
            const iconColor = groupStatus === 'optimal' ? 'text-green-600'                      : groupStatus === 'critical' ? 'text-red-600'                    : 'text-yellow-600';
            const badgeCls  = groupStatus === 'optimal' ? 'bg-green-100 text-green-700'         : groupStatus === 'critical' ? 'bg-red-100 text-red-700'          : 'bg-yellow-100 text-yellow-700';
            const badgeLabel = groupStatus === 'optimal' ? 'Optimal' : groupStatus === 'critical' ? 'Critical' : 'Needs Attention';

            return (
              <div key={groupId} className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                {/* Group Header */}
                <div
                  className="p-6 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors"
                  onClick={() => toggleGroup(groupId)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4 flex-1">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${iconBg}`}>
                        <i className={`${cfg?.icon ?? 'ri-bar-chart-grouped-line'} text-xl ${iconColor}`}></i>
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center space-x-2">
                          <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                            {cfg?.label ?? groupId.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                          </h3>
                          <button
                            onClick={e => { e.stopPropagation(); onTooltipShow(cfg?.label ?? groupId, `${groupId} parameter group tracking baseline vs current performance.`); }}
                            className="text-gray-400 hover:text-blue-500"
                          >
                            <i className="ri-information-line text-sm"></i>
                          </button>
                        </div>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{subParams.length} sub-parameter{subParams.length !== 1 ? 's' : ''} tracked</p>
                        {groupId === 'page_experience_performance' && (
                          <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                            INP requires real user traffic data. Not available for low-traffic sites.
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center space-x-4">
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${badgeCls}`}>{badgeLabel}</span>
                      <i className={`ri-arrow-${isExpanded ? 'up' : 'down'}-s-line text-2xl text-gray-400`}></i>
                    </div>
                  </div>
                </div>

                {/* Expanded Table */}
                {isExpanded && (
                  <div className="border-t border-gray-200 dark:border-gray-700">
                    {subParams.length === 0 ? (
                      <div className="px-6 py-8 text-center">
                        <p className="text-sm text-gray-500 dark:text-gray-400">No parameters match the current filter</p>
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0">
                            <tr>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Sub-Parameter</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Baseline</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Current</th>
                              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Change</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                            {subParams.map((sp, idx) => (
                              <tr key={`${sp.sub_parameter}-${idx}`} className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                                {/* Sub-Parameter Name */}
                                <td className="px-6 py-4">
                                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                                    {getParamLabel(sp.sub_parameter)}
                                  </span>
                                </td>

                                {/* Baseline */}
                                <td className="px-6 py-4">
                                  <div className="flex flex-col gap-1">
                                    {sp.baseline.value ? (
                                      <span className="text-sm font-medium text-gray-900 dark:text-white">
                                        {sp.baseline.value}
                                      </span>
                                    ) : null}
                                    <StatusBadge status={sp.baseline.status} />
                                    <span className="text-xs text-gray-400 dark:text-gray-500">
                                      Baseline — {formatDate(sp.baseline.snapshot_date)}
                                    </span>
                                  </div>
                                </td>

                                {/* Current */}
                                <td className="px-6 py-4">
                                  {sp.current ? (
                                    <div className="flex flex-col gap-1">
                                      {sp.current.value ? (
                                        <span className="text-sm font-medium text-gray-900 dark:text-white">
                                          {sp.current.value}
                                        </span>
                                      ) : null}
                                      <StatusBadge status={sp.current.status} />
                                      <span className="text-xs text-gray-400 dark:text-gray-500">
                                        Last scan — {formatDate(sp.current.snapshot_date)}
                                      </span>
                                    </div>
                                  ) : (
                                    <span className="text-sm text-gray-400 dark:text-gray-500 italic">No re-scan yet</span>
                                  )}
                                </td>

                                {/* Change */}
                                <td className="px-6 py-4 text-center">
                                  {sp.delta ? (
                                    <>
                                      {sp.delta.direction === 'improved' && (
                                        <span className="inline-flex flex-col items-center text-green-600 dark:text-green-400">
                                          <i className="ri-arrow-up-line text-xl"></i>
                                          <span className="text-xs font-medium">Improved</span>
                                        </span>
                                      )}
                                      {sp.delta.direction === 'regressed' && (
                                        <span className="inline-flex flex-col items-center text-red-600 dark:text-red-400">
                                          <i className="ri-arrow-down-line text-xl"></i>
                                          <span className="text-xs font-medium">Regressed</span>
                                        </span>
                                      )}
                                      {sp.delta.direction === 'unchanged' && (
                                        <span className="text-gray-400 dark:text-gray-500 text-lg">—</span>
                                      )}
                                    </>
                                  ) : null}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Next Step CTA */}
      <div className="bg-gradient-to-r from-purple-500 to-blue-600 rounded-xl p-6 shadow-lg">
        <div className="flex items-center justify-between text-white">
          <div>
            <h3 className="text-xl font-bold mb-2">Ready to Plan and Implement Your SEO Strategy?</h3>
            <p className="text-purple-100">Based on your baseline assessment, let's define achievable SMART goals</p>
          </div>
          <button
            onClick={() => navigate('/dashboard#goal-setting')}
            className="px-6 py-3 bg-white text-purple-600 rounded-lg font-semibold hover:bg-purple-50 transition-colors whitespace-nowrap"
          >
            Set Goals →
          </button>
        </div>
      </div>

      {/* Detail Popups */}
      <DetailPopup isOpen={isKeywordPopupOpen} onClose={() => setIsKeywordPopupOpen(false)} title="Keyword Rankings (Tracked Keywords)">
        <div className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div><p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Metric</p><p className="text-sm text-gray-900 dark:text-white font-medium">Keyword Rankings</p></div>
            <div><p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Type</p><p className="text-sm text-gray-900 dark:text-white font-medium">Position/Numeric</p></div>
            <div><p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Goal</p><p className="text-sm text-gray-900 dark:text-white font-medium">Rank in Top 10</p></div>
            <div><p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Data Source</p><p className="text-sm text-gray-900 dark:text-white font-medium">Google Search Console</p></div>
          </div>
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Keyword Performance</p>
            <div className="space-y-2 max-h-96 overflow-y-auto pr-2">
              <table className="w-full text-left border-collapse">
                <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0 z-10">
                  <tr>
                    <th className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase">Keyword</th>
                    <th className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase text-right">Rank</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-800">
                  {kwMetrics?.ranked_keywords?.length > 0 ? (
                    kwMetrics.ranked_keywords.map((kw: any, idx: number) => {
                      const isNearMe = String(kw.keyword || '').toLowerCase().includes('near me');
                      const isNA = isNearMe || kw.position === 'N/A' || kw.position === 'NA';
                      return (
                        <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                          <td className="px-4 py-3 text-sm text-gray-900 dark:text-white font-medium">{kw.keyword}</td>
                          <td className="px-4 py-3 text-sm text-right">
                            <span className={`font-bold ${isNA ? 'text-gray-400' : 'text-teal-600'}`}>
                              {isNA ? 'N/A' : /^\d+(\.\d+)?$/.test(String(kw.position)) ? `#${kw.position}` : kw.position}
                            </span>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={2} className="px-4 py-4 text-sm text-gray-500 italic text-center">
                        No keyword data available yet
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </DetailPopup>

      <DetailPopup isOpen={isSerpPopupOpen} onClose={() => { setIsSerpPopupOpen(false); setSerpTab('features'); }} title="Captured Visibility (Tracked Keywords)">
        <div className="flex flex-col h-full overflow-hidden space-y-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Brand presence across search features for your locked keywords compared to baseline.
          </p>

          {/* Baseline -> Current -> Delta Banner */}
          <div className="grid grid-cols-4 gap-3 bg-gray-50 dark:bg-gray-800/80 p-3.5 rounded-xl border border-gray-200 dark:border-gray-700">
            <div className="text-center">
              <p className="text-xl font-bold text-gray-700 dark:text-gray-200">
                {baselineActiveCount} <span className="text-sm font-normal text-gray-400">/ {serpApplicableCount}</span>
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Baseline Captured</p>
            </div>
            <div className="text-center">
              <p className="text-xl font-bold text-teal-600 dark:text-teal-400">
                {serpActiveCount} <span className="text-sm font-normal text-teal-400/70">/ {serpApplicableCount}</span>
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Current Captured</p>
            </div>
            <div className="text-center">
              <p className={`text-xl font-bold ${
                serpDelta > 0
                  ? 'text-green-600 dark:text-green-400'
                  : serpDelta < 0
                  ? 'text-red-600 dark:text-red-400'
                  : 'text-gray-400'
              }`}>
                {serpDelta > 0 ? `+${serpDelta}` : serpDelta === 0 ? '0' : `${serpDelta}`}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Change (Δ)</p>
            </div>
            <div className="text-center">
              <p className="text-xl font-bold text-purple-600 dark:text-purple-400">{serpApplicableCount}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Applicable Features</p>
            </div>
          </div>

          <div className="flex border-b border-gray-200 dark:border-gray-700 mb-4">
            <button
              onClick={() => setSerpTab('features')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                serpTab === 'features'
                  ? 'border-purple-500 text-purple-600 dark:text-purple-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              <i className="ri-table-line mr-1"></i> Feature Overview ({serpFeatureList.length})
            </button>
            <button
              onClick={() => setSerpTab('keywords')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                serpTab === 'keywords'
                  ? 'border-purple-500 text-purple-600 dark:text-purple-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              <i className="ri-key-line mr-1"></i> Keyword Mapping
              <span className="ml-1 px-1.5 py-0.5 rounded-full bg-purple-100 dark:bg-purple-900/40 text-purple-600 dark:text-purple-400 text-xs font-semibold">
                {serpMetrics?.keyword_details?.length ?? 0}
              </span>
            </button>
          </div>

          {serpTab === 'features' && (
            <div className="space-y-2.5 max-h-96 overflow-y-auto pr-2">
              {serpFeatureList.map(f => (
                <div key={f.key} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/60 rounded-xl border border-gray-100 dark:border-gray-700">
                  <div className="flex items-center space-x-3 min-w-0 flex-1">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      f.present
                        ? 'bg-teal-100 dark:bg-teal-900/40 text-teal-600 dark:text-teal-400'
                        : f.missing
                        ? 'bg-amber-100 dark:bg-amber-900/40 text-amber-600 dark:text-amber-400'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-400'
                    }`}>
                      <i className={`${f.icon} text-base`}></i>
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center space-x-2">
                        <span className="text-sm font-medium text-gray-900 dark:text-white truncate">{f.name}</span>
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300 font-medium">
                          {f.type}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">{f.description}</p>
                    </div>
                  </div>
                  <span className={`ml-3 px-2.5 py-1 rounded-full text-xs font-semibold flex-shrink-0 ${
                    f.present
                      ? 'bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300'
                      : f.missing
                      ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'
                      : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
                  }`}>
                    {f.present ? 'Active' : f.missing ? 'Missing' : 'Not Applicable'}
                  </span>
                </div>
              ))}
            </div>
          )}

          {serpTab === 'keywords' && (
            <div className="overflow-x-auto border border-gray-100 dark:border-gray-700 rounded-xl max-h-96 overflow-y-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-900/50 sticky top-0 z-10">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">#</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Locked Keyword</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">SERP Features Found</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Your Brand Presence</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">Rank</th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-100 dark:divide-gray-700">
                  {(serpMetrics?.keyword_details || []).map((row: any, idx: number) => {
                    const features: string[] = row.features || [];
                    const yourPresence: string[] = row.your_presence || [];
                    const hasFeaturesFound = features.length > 0;
                    const hasBrandPresence = yourPresence.length > 0;
                    return (
                      <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                        <td className="px-4 py-3 text-xs text-gray-400 dark:text-gray-500">{idx + 1}</td>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-gray-100 max-w-[200px]">
                          <span className="break-words">{row.keyword}</span>
                        </td>
                        <td className="px-4 py-3">
                          {hasFeaturesFound ? (
                            <div className="flex flex-wrap gap-1">
                              {features.map((f: string, fi: number) => (
                                <span key={fi} className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 font-medium">
                                  {SERP_FEATURE_META[f]?.name || humanizeSerpFeature(f)}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <span className="text-xs text-gray-400 italic">No special features</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {hasFeaturesFound ? (
                            hasBrandPresence ? (
                              <div className="flex flex-wrap gap-1">
                                {yourPresence.map((f: string, fi: number) => (
                                  <span key={fi} className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300 font-medium">
                                    <i className="ri-checkbox-circle-fill mr-0.5"></i>{SERP_FEATURE_META[f]?.name || humanizeSerpFeature(f)}
                                  </span>
                                ))}
                              </div>
                            ) : (
                              <span className="inline-flex items-center text-xs font-medium text-amber-600">
                                <i className="ri-close-circle-line mr-1"></i> Not present
                              </span>
                            )
                          ) : (
                            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400">
                              <i className="ri-eye-off-line"></i> Not Visible
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-center text-sm font-semibold text-gray-700 dark:text-gray-300">
                          {row.position != null ? `#${row.position}` : '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </DetailPopup>

      <DetailPopup isOpen={isBlogPopupOpen} onClose={() => setIsBlogPopupOpen(false)} title="Organic Traffic">
        <div className="space-y-4">
          {trafficMetrics?.data_available ? (
            <>
              <div className="grid grid-cols-3 gap-4">
                <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg text-center">
                  <p className="text-2xl font-bold text-teal-600">{trafficMetrics.total_clicks}</p>
                  <p className="text-xs text-gray-500 mt-1">Total Clicks</p>
                </div>
                <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg text-center">
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">{trafficMetrics.total_impressions}</p>
                  <p className="text-xs text-gray-500 mt-1">Impressions</p>
                </div>
                <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg text-center">
                  <p className="text-2xl font-bold text-purple-600">{trafficMetrics.avg_session_duration || '—'}</p>
                  <p className="text-xs text-gray-500 mt-1">Avg Session</p>
                </div>
              </div>
              {(trafficMetrics.top_pages || []).length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Top Pages</p>
                  {trafficMetrics.top_pages.map((p: any, i: number) => (
                    <div key={i} className="flex justify-between p-2 text-sm border-b border-gray-100 dark:border-gray-700">
                      <span className="text-gray-900 dark:text-white truncate flex-1" title={p.page === '/' ? `${siteDomain}/` : `${siteDomain}${p.page}`}>
                        {p.page === '/' ? `${siteDomain}/` : `${siteDomain}${p.page}`}
                      </span>
                      <span className="font-bold ml-2">{p.visitors}</span>
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : (
            <p className="text-sm text-gray-500 italic text-center">Connect Google Search Console to view traffic data.</p>
          )}
        </div>
      </DetailPopup>
    </div>
  );
}
